import type { Dialog } from '@base-ui/react'
import {
	DropdownMenuPortal,
	Root as DropdownMenuRoot,
	DropdownMenuTrigger
} from '@radix-ui/react-dropdown-menu'
import { useCallback } from 'react'
import { toast } from 'react-hot-toast'
import { useLocation, useNavigate } from 'react-router'
import { DialogTrigger } from '@/components/ui/dialog'
import { DropdownContent, DropdownItem } from '@/components/ui/dropdown-atoms'
import { DotsHorizontalIcon } from '@/components/ui/icons'
import type {
	MMSubmission,
	SelectedMMListing
} from '@/features/luxe-submissions/types'
import { getQuoteGenerationBlockerLabelTKey } from '@/features/luxe-submissions/utils'
import useCopyToClipboard from '@/hooks/use-copy-to-clipboard'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

interface MMParkingLotActionsDropdownProps {
	dataItem: MMSubmission
	generateQuoteHandle: Dialog.Handle<{ listings: SelectedMMListing[] }>
	listingActivityHandle: Dialog.Handle<{ listingId: string }>
	askSecondReviewHandle: Dialog.Handle<{ submissionIds: string[] }>
}

function MMParkingLotActionsDropdown({
	dataItem,
	generateQuoteHandle,
	listingActivityHandle,
	askSecondReviewHandle
}: MMParkingLotActionsDropdownProps) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const { copyToClipboard } = useCopyToClipboard(() => {
		toast.success(t('luxe_submissions:quote_link_copied'))
	})

	// Navigate to listing
	const handleViewListing = useCallback(
		(listingId: string) => {
			navigate(
				routes.LUXE_SUBMISSIONS_MM_PARKING_LOT_DETAILS.handle.fullPath.replace(
					':id',
					listingId
				),
				{ state: { fromSearch: location.search } }
			)
		},
		[navigate, location.search]
	)

	const handleCopyQuoteLink = useCallback(
		(quoteLink: string | null) => {
			if (quoteLink) {
				copyToClipboard(quoteLink)
			}
		},
		[copyToClipboard]
	)

	const getActions = useCallback(
		(dataItem: MMSubmission) => {
			switch (dataItem.mmStatus) {
				case 'pre_onboarding':
					return [
						{
							label: t('luxe_submissions:generate_quote'),
							key: 'generate_quote',
							disabled:
								!dataItem.availableActions.includes(
									'generate_quote'
								)
						},
						...(dataItem.hadSecondReview
							? []
							: [
									{
										label: t(
											'luxe_submissions:ask_second_review'
										),
										key: 'ask_second_review',
										disabled:
											!dataItem.availableActions.includes(
												'ask_second_review'
											)
									}
								]),
						{
							label: t('luxe_submissions:see_details'),
							key: 'see_details',
							action: () => handleViewListing(dataItem.id),
							disabled:
								!dataItem.availableActions.includes(
									'see_details'
								)
						},
						{
							label: t('luxe_submissions:show_activity'),
							key: 'show_activity',
							disabled:
								!dataItem.availableActions.includes(
									'show_activity'
								)
						}
					]
				case 'failed_standards':
					return [
						...(dataItem.hadSecondReview
							? []
							: [
									{
										label: t(
											'luxe_submissions:ask_second_review'
										),
										key: 'ask_second_review',
										disabled:
											!dataItem.availableActions.includes(
												'ask_second_review'
											)
									}
								]),
						{
							label: t('luxe_submissions:see_details'),
							key: 'see_details',
							action: () => handleViewListing(dataItem.id),
							disabled:
								!dataItem.availableActions.includes(
									'see_details'
								)
						},
						{
							label: t('luxe_submissions:show_activity'),
							key: 'show_activity',
							disabled:
								!dataItem.availableActions.includes(
									'show_activity'
								)
						}
					]
				case 'ready_for_inspection':
					return [
						{
							label: t('luxe_submissions:copy_quote_link'),
							key: 'copy_quote_link',
							action: () =>
								handleCopyQuoteLink(dataItem.quoteLink),
							disabled:
								!dataItem.availableActions.includes(
									'copy_quote_link'
								)
						},
						{
							label: t('luxe_submissions:see_details'),
							key: 'see_details',
							action: () => handleViewListing(dataItem.id),
							disabled:
								!dataItem.availableActions.includes(
									'see_details'
								)
						},
						{
							label: t('luxe_submissions:show_activity'),
							key: 'show_activity',
							disabled:
								!dataItem.availableActions.includes(
									'show_activity'
								)
						}
					]
				case 'back_in_review':
					return [
						{
							label: t('luxe_submissions:generate_quote'),
							key: 'generate_quote',
							disabled: true
						},
						{
							label: t('luxe_submissions:see_details'),
							key: 'see_details',
							action: () => handleViewListing(dataItem.id),
							disabled:
								!dataItem.availableActions.includes(
									'see_details'
								)
						},
						{
							label: t('luxe_submissions:show_activity'),
							key: 'show_activity',
							disabled:
								!dataItem.availableActions.includes(
									'show_activity'
								)
						}
					]
				default:
					return []
			}
		},
		[t, handleViewListing, handleCopyQuoteLink]
	)

	const actions = getActions(dataItem)

	// The backend can't quote this listing (e.g. missing listing info)
	const isQuoteGenerationUnavailable = dataItem.canGenerateQuote === false

	return (
		<DropdownMenuRoot>
			<DropdownMenuTrigger asChild>
				<button
					className='flex cursor-pointer items-center justify-center text-neutral-500 hover:text-neutral-700'
					type='button'
				>
					<DotsHorizontalIcon size={20} />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownContent align='end' className='w-60' sideOffset={4}>
					{actions.map((action) => {
						if (action.key === 'generate_quote') {
							return (
								<DialogTrigger
									disabled={
										action.disabled ||
										isQuoteGenerationUnavailable
									}
									handle={generateQuoteHandle}
									id='trigger-dropdown'
									key={action.key}
									nativeButton={false}
									payload={{
										listings: [
											{
												id: dataItem.id,
												hostEmail: dataItem.hostEmail,
												airbnbListingId:
													dataItem.airbnbListingId
											}
										]
									}}
									render={
										<DropdownItem className='flex flex-col whitespace-normal text-neutral-900 data-disabled:opacity-50'>
											{action.label}
											{/* The reason can be long, so it wraps under the label */}
											{isQuoteGenerationUnavailable && (
												<span className='font-normal text-xs'>
													{t(
														getQuoteGenerationBlockerLabelTKey(
															dataItem.quoteGenerationBlocker
														)
													)}
												</span>
											)}
										</DropdownItem>
									}
								/>
							)
						}

						if (action.key === 'ask_second_review') {
							return (
								<DialogTrigger
									disabled={action.disabled}
									handle={askSecondReviewHandle}
									id='trigger-ask-second-review'
									key={action.key}
									nativeButton={false}
									payload={{
										submissionIds: [dataItem.id]
									}}
									render={
										<DropdownItem className='text-neutral-900 data-disabled:opacity-50'>
											{action.label}
										</DropdownItem>
									}
								/>
							)
						}

						if (action.key === 'show_activity') {
							return (
								<DialogTrigger
									disabled={action.disabled}
									handle={listingActivityHandle}
									id='trigger-activity'
									key={action.key}
									nativeButton={false}
									payload={{
										listingId: dataItem.id
									}}
									render={
										<DropdownItem className='text-neutral-900 data-disabled:opacity-50'>
											{action.label}
										</DropdownItem>
									}
								/>
							)
						}

						return (
							<DropdownItem
								className='text-neutral-900 data-disabled:opacity-50'
								disabled={action.disabled}
								key={action.key}
								onClick={action.action}
							>
								{action.label}
							</DropdownItem>
						)
					})}
				</DropdownContent>
			</DropdownMenuPortal>
		</DropdownMenuRoot>
	)
}

export { MMParkingLotActionsDropdown }
